import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { MapPin, Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';

const ContactPage = () => {
  const { settings } = useSiteData();
  const { t } = useLang();
  const c = settings?.contact || {};
  const header = settings?.page_headers?.contact || {};
  const cp = settings?.contact_page || {};
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    const stored = JSON.parse(localStorage.getItem('contact_messages') || '[]');
    stored.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('contact_messages', JSON.stringify(stored));
    setSent(true);
    setForm({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with the South East State of Somalia State House — office address, email, phone and citizen feedback form."
        keywords="Somalia government contact, State House contact, South East State email, citizen feedback Somalia"
      />
      <PageHeader title={header.title || t('Contact')} subtitle={header.subtitle} crumbs={[{ label: t('Contact') }]} />
      <section className="bg-[#FAF6EC] py-14">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#C9A86A]">{cp.eyebrow || t('Get in Touch')}</div>
            <h2 className="font-serif text-3xl font-bold text-[#0B2545]">{cp.title || t('We are here to listen.')}</h2>
            <div className="mt-4 h-[3px] w-16 bg-[#C9A86A]" />
            <p className="mt-6 text-[14px] leading-relaxed text-[#0B2545]/75">{cp.description}</p>
            <ul className="mt-8 space-y-5 text-[14px] text-[#0B2545]/85">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#C9A86A] shadow-sm"><MapPin size={18} /></span>
                <div>
                  <div className="font-semibold text-[#0B2545]">{t('Address')}</div>
                  <div className="text-[#0B2545]/70">{c.address}</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#C9A86A] shadow-sm"><Mail size={18} /></span>
                <div>
                  <div className="font-semibold text-[#0B2545]">{t('Email')}</div>
                  <a href={`mailto:${c.email}`} className="text-[#0B2545]/70 hover:text-[#C9A86A]">{c.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#C9A86A] shadow-sm"><Phone size={18} /></span>
                <div>
                  <div className="font-semibold text-[#0B2545]">{t('Phone')}</div>
                  <div className="text-[#0B2545]/70">{c.phone}</div>
                </div>
              </li>
            </ul>
          </div>

          <form onSubmit={submit} className="rounded-lg border border-[#E6DDC8] bg-white p-8 shadow-sm lg:col-span-3">
            <h3 className="font-serif text-xl font-bold text-[#0B2545]">{cp.form_title || t('Send a Message')}</h3>
            <p className="mt-1 text-[13px] text-[#0B2545]/60">{cp.form_subtitle}</p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">{t('Full Name')} *</label>
                <input required value={form.name} onChange={(e) => update('name', e.target.value)} className="w-full rounded-md border border-[#E6DDC8] bg-[#FAF6EC] px-3 py-2.5 text-[14px] text-[#0B2545] outline-none focus:border-[#C9A86A]" />
              </div>
              <div>
                <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">{t('Email')} *</label>
                <input type="email" required value={form.email} onChange={(e) => update('email', e.target.value)} className="w-full rounded-md border border-[#E6DDC8] bg-[#FAF6EC] px-3 py-2.5 text-[14px] text-[#0B2545] outline-none focus:border-[#C9A86A]" />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">{t('Subject')}</label>
              <input value={form.subject} onChange={(e) => update('subject', e.target.value)} className="w-full rounded-md border border-[#E6DDC8] bg-[#FAF6EC] px-3 py-2.5 text-[14px] text-[#0B2545] outline-none focus:border-[#C9A86A]" />
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-[12.5px] font-medium text-[#0B2545]">{t('Message')} *</label>
              <textarea required rows={6} value={form.message} onChange={(e) => update('message', e.target.value)} className="w-full resize-none rounded-md border border-[#E6DDC8] bg-[#FAF6EC] px-3 py-2.5 text-[14px] text-[#0B2545] outline-none focus:border-[#C9A86A]" />
            </div>
            <div className="mt-6 flex items-center justify-between gap-3">
              {sent ? (
                <span className="inline-flex items-center gap-2 text-[13px] font-medium text-green-700"><CheckCircle2 size={16} /> {t('Message sent. Thank you!')}</span>
              ) : (
                <span className="text-[12px] text-[#0B2545]/55">{cp.reply_note}</span>
              )}
              <button type="submit" className="group inline-flex items-center gap-2 rounded-md bg-[#0B2545] px-5 py-2.5 text-[14px] font-medium text-white hover:bg-[#08203a]">
                {t('Send Message')}
                <Send size={14} className="transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
